import { c as _c } from "react/compiler-runtime";
import type { Entity } from '@onefootprint/request-types/dashboard';
import type { ImageComparisonBlockConfig } from '@onefootprint/request-types/smart-blocks';
import { useArtifactEntity } from '../artifact-entity-context';
import ImageComparison from './image-comparison/image-comparison';
import ImagePane, { type ImagePaneProps } from './image-comparison/image-pane';
import SourceTile, { type SourceTileProps } from './image-comparison/source-tile';
import type { ComparisonImage, ComparisonSelection } from './image-comparison/types';
import useVaultImage from './image-comparison/use-vault-image';
const VaultSourceTile = t0 => {
  const $ = _c(9);
  let entity;
  let props;
  if ($[0] !== t0) {
    ({
      entity,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = entity;
    $[2] = props;
  } else {
    entity = $[1];
    props = $[2];
  }
  const source = useVaultImage(entity, props.image.documentIdentifier);
  let t1;
  if ($[3] !== props.image || $[4] !== source) {
    t1 = {
      ...props.image,
      ...source
    };
    $[3] = props.image;
    $[4] = source;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== props || $[7] !== t1) {
    t2 = <SourceTile {...props} image={t1} />;
    $[6] = props;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
};
const VaultImagePane = t0 => {
  const $ = _c(9);
  let entity;
  let props;
  if ($[0] !== t0) {
    ({
      entity,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = entity;
    $[2] = props;
  } else {
    entity = $[1];
    props = $[2];
  }
  const source = useVaultImage(entity, props.image.documentIdentifier);
  let t1;
  if ($[3] !== props.image || $[4] !== source) {
    t1 = {
      ...props.image,
      ...source
    };
    $[3] = props.image;
    $[4] = source;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== props || $[7] !== t1) {
    t2 = <ImagePane {...props} image={t1} />;
    $[6] = props;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
};
const ImageComparisonContent = t0 => {
  const $ = _c(20);
  const {
    config,
    entity,
    selection
  } = t0;
  let t1;
  if ($[0] !== config.images || $[1] !== entity) {
    let t2;
    if ($[3] !== entity) {
      t2 = image => ({
        ...image,
        fields: image.fields ?? [],
        width: 1000,
        height: 625,
        status: entity ? "unavailable" : "no-entity"
      });
      $[3] = entity;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = config.images.map(t2);
    $[0] = config.images;
    $[1] = entity;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const images = t1;
  const t2 = config.title ?? undefined;
  let t3;
  let t4;
  if ($[5] !== entity) {
    t3 = props => entity ? <VaultSourceTile {...props} entity={entity} /> : <SourceTile {...props} />;
    t4 = props_0 => entity ? <VaultImagePane key={props_0.image.id} {...props_0} entity={entity} /> : <ImagePane key={props_0.image.id} {...props_0} />;
    $[5] = entity;
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  let t5;
  if ($[8] !== config.results || $[9] !== images || $[10] !== selection || $[11] !== t2 || $[12] !== t3 || $[13] !== t4) {
    t5 = <ImageComparison images={images} title={t2} initialSelection={selection} results={config.results} renderSource={t3} renderImage={t4} />;
    $[8] = config.results;
    $[9] = images;
    $[10] = selection;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== config.note) {
    t6 = config.note && <p className="px-4 pt-2 text-caption-2 text-tertiary">{config.note}</p>;
    $[15] = config.note;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== t5 || $[18] !== t6) {
    t7 = <div>{t5}{t6}</div>;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  return t7;
};
const ArtifactImageComparisonBlock = t0 => {
  const $ = _c(10);
  const {
    config
  } = t0;
  const entity = useArtifactEntity();
  let selection;
  let t1;
  if ($[0] !== config.images || $[1] !== config.initialSelection || $[2] !== entity?.id) {
    selection = config.initialSelection ? [config.initialSelection.left ?? null, config.initialSelection.right ?? null] : undefined;
    t1 = JSON.stringify([entity?.id, config.images.map(_temp), selection]);
    $[0] = config.images;
    $[1] = config.initialSelection;
    $[2] = entity?.id;
    $[3] = selection;
    $[4] = t1;
  } else {
    selection = $[3];
    t1 = $[4];
  }
  const identity = t1;
  let t2;
  if ($[5] !== config || $[6] !== entity || $[7] !== identity || $[8] !== selection) {
    t2 = <ImageComparisonContent key={identity} config={config} entity={entity} selection={selection} />;
    $[5] = config;
    $[6] = entity;
    $[7] = identity;
    $[8] = selection;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};
export default ArtifactImageComparisonBlock;
function _temp(image) {
  return [image.id, image.documentIdentifier];
}